import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { SettingsPermissions } from "@redotech/redo-model/permissions";
import { ReturnOptionMethod } from "@redotech/redo-model/return-flow/return-option";
import {
  SubmitClaimState,
  returnOptionLabels,
} from "@redotech/redo-return-ui/flows/return-flow/submit-claim";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import {
  ProductAutocomplete,
  ProductVariantInfo,
} from "@redotech/redo-web/product-autocomplete";
import { Text } from "@redotech/redo-web/text";
import { produce } from "immer";
import { memo, useContext, useMemo } from "react";
import { useProductAutocomplete } from "./hooks/use-product-autocomplete";
import { returnOptionMethods } from "./return";

export const SubmitClaimDetails = memo(function Details({
  state,
  setState,
}: StepTypeDetailsProps<SubmitClaimState>) {
  const team = useContext(TeamContext);
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );

  const returnOptionMethodsListItems: RedoListItem<
    ReturnOptionMethod["type"]
  >[] = useMemo(
    () =>
      returnOptionMethods.map((method) => ({
        id: method,
        value: method,
        type: "text" as const,
        text: returnOptionLabels(method) || "",
      })),
    [],
  );

  const { productsState, selectedProduct, onSelectedProductChange } =
    useProductAutocomplete(
      team?._id || "",
      state.option.variantExchangeAddOnVariantId,
    );

  const handleActionChange = useHandler((method: ReturnOptionMethod["type"]) =>
    setState((state) =>
      produce(state, (state) => {
        state.option.method = method;
      }),
    ),
  );
  const handleRequireSendBack = useHandler((value: boolean) =>
    setState((state) =>
      produce(state, (state) => {
        state.option.requireSendBack = value;
      }),
    ),
  );
  const handleExchangeShipping = useHandler((value: boolean) =>
    setState((state) =>
      produce(state, (state) => {
        state.option.chargeCustomerForNewOrderShipping = value;
      }),
    ),
  );
  const handleVariantExchangeVariantId = useHandler(
    (value: ProductVariantInfo | null | undefined) => {
      onSelectedProductChange(value ?? null);
      if (value) {
        setState((state) =>
          produce(state, (state) => {
            state.option.variantExchangeAddOnVariantId = value.variantId;
          }),
        );
      }
    },
  );
  const handleVariantExchangeAddOnTitle = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        state.option.variantExchangeAddOnTitle = value;
      }),
    );
  });
  const handleVariantExchangeAddOnAcceptButton = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        state.option.variantExchangeAddOnAcceptButton = value;
      }),
    );
  });
  const handleVariantExchangeAddOnRejectButton = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        state.option.variantExchangeAddOnRejectButton = value;
      }),
    );
  });

  // Require team context to be available
  if (!team) {
    return null;
  }

  return (
    <>
      <Text fontSize="xs" textColor="secondary">
        Compensation Method
      </Text>
      <RedoSingleSelectDropdownInput
        disabled={!canEditReturnsSettings}
        options={returnOptionMethodsListItems}
        optionSelected={({ item }) => handleActionChange(item.value)}
        selectedItem={returnOptionMethodsListItems.find(
          (item) => item.value === state.option.method,
        )}
        size="xs"
      />
      <RedoToggle
        disabled={!canEditReturnsSettings}
        label="Require customer to send back item?"
        setValue={handleRequireSendBack}
        value={state.option.requireSendBack || false}
      />
      {state.option.method === ReturnOptionMethod.EXCHANGE && (
        <>
          <RedoToggle
            description="If enabled, customer will be charged for outgoing shipping of
            exchange orders."
            disabled={!canEditReturnsSettings}
            label="Charge customer for new order shipping"
            setValue={handleExchangeShipping}
            value={state.option.chargeCustomerForNewOrderShipping || false}
          />
          <RedoToggle
            description="Offer the customer an add-on product if they do same-item exchange.
            The customer will be charged the cost of the product"
            disabled={!canEditReturnsSettings}
            label="Same-item exchange add-on"
            setValue={() => {
              setState((state) =>
                produce(state, (state) => {
                  state.option.variantExchangeAddOn =
                    !state.option.variantExchangeAddOn;
                }),
              );
            }}
            value={state.option.variantExchangeAddOn ?? false}
          />
          {state.option.variantExchangeAddOn && (
            <Flex dir="column" gap="lg">
              <ProductAutocomplete
                label="Product to add to exchange"
                productsState={productsState}
                setValue={handleVariantExchangeVariantId}
                value={selectedProduct}
              />
              <RedoTextInput
                label="Title text"
                setValue={handleVariantExchangeAddOnTitle}
                value={state.option.variantExchangeAddOnTitle ?? ""}
              />
              <Flex dir="row" gap="md">
                <RedoTextInput
                  fullWidth
                  label="Accept button text"
                  setValue={handleVariantExchangeAddOnAcceptButton}
                  value={state.option.variantExchangeAddOnAcceptButton ?? ""}
                />
                <RedoTextInput
                  fullWidth
                  label="Reject button text"
                  setValue={handleVariantExchangeAddOnRejectButton}
                  value={state.option.variantExchangeAddOnRejectButton ?? ""}
                />
              </Flex>
            </Flex>
          )}
        </>
      )}
    </>
  );
});
